import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_BASE_URL?.replace("/api", "") || "";

/**
 * Socket.IO subscription hook for affiliate dashboard real-time updates.
 *
 * @param {string} affiliateId - Affiliate record ID
 * @param {function} onUpdate - Callback when any affiliate event fires
 */
const useAffiliateDashboard = (affiliateId, onUpdate) => {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!affiliateId) return;

    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("affiliate:join-dashboard", { affiliateId });
    });

    // Listen for all affiliate events
    const events = [
      "affiliate:new-click",
      "affiliate:new-conversion",
      "affiliate:commission-updated",
      "affiliate:payout-processed",
      "affiliate:tier-changed",
    ];

    events.forEach((event) => {
      socket.on(event, () => {
        if (onUpdate) onUpdate();
      });
    });

    // On reconnect, re-join room and refresh data
    socket.on("reconnect", () => {
      socket.emit("affiliate:join-dashboard", { affiliateId });
      if (onUpdate) onUpdate();
    });

    return () => {
      socket.emit("affiliate:leave-dashboard", { affiliateId });
      socket.disconnect();
    };
  }, [affiliateId]);

  return socketRef.current;
};

export default useAffiliateDashboard;
